import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface UserProfile {
  id: string;
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
  email: string | null;
  role: string;
  suspended: boolean;
}

interface UserPermissionsManagerProps {
  isOpen: boolean;
  onClose: () => void;
  user: UserProfile | null;
  onPermissionsUpdated: () => void;
}

// Available tabs in the application
const AVAILABLE_TABS = [
  { id: 'dashboard', name: 'Dashboard', description: 'Main dashboard view' },
  { id: 'clients', name: 'Clients', description: 'Client management' },
  { id: 'people', name: 'People', description: 'People management' },
  { id: 'proposals', name: 'Proposals', description: 'Proposal management' },
  { id: 'projects', name: 'Projects', description: 'Project management' },
  { id: 'activities', name: 'Activities', description: 'Activity tracking' },
  { id: 'xero-lite', name: 'Xero-Lite', description: 'Accounting management (Invoices, Bills, Banking, Reports)' },
  { id: 'users', name: 'Users', description: 'User management' },
  { id: 'initiatives', name: 'Initiatives', description: 'Initiative management' },
  { id: 'wisdrop', name: 'Wisdrop', description: 'Reports management and analytics' },
  { id: 'settings', name: 'Settings', description: 'System settings' }
];

export function UserPermissionsManager({ 
  isOpen, 
  onClose, 
  user, 
  onPermissionsUpdated 
}: UserPermissionsManagerProps) {
  const { toast } = useToast();
  const [allowedTabs, setAllowedTabs] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Check if user is stamatis - super admin
  const isSuperAdmin = user?.email?.toLowerCase().includes('stamatis') || user?.first_name?.toLowerCase() === 'stamatis';

  useEffect(() => {
    if (isOpen && user) {
      // Reset state when opening modal
      setAllowedTabs([]);
      setIsLoading(false);
      fetchUserPermissions();
    } else if (!isOpen) {
      // Clear state when closing modal
      setAllowedTabs([]);
    }
  }, [isOpen, user]);

  const fetchUserPermissions = async () => {
    if (!user) return;

    try {
      console.log('Fetching permissions for user:', user.user_id, user.first_name, user.last_name);
      
      const { data, error } = await supabase
        .from('user_permissions')
        .select('module')
        .eq('user_id', user.user_id);

      if (error) throw error;

      const tabs = data?.map(p => p.module) || [];
      console.log('Fetched permissions:', tabs);
      setAllowedTabs(tabs);
    } catch (error: any) {
      console.error('Error fetching permissions:', error);
      toast({
        title: "Error",
        description: "Failed to load user permissions",
        variant: "destructive"
      });
    }
  };

  const handleTabToggle = (tabId: string, checked: boolean) => {
    if (checked) {
      setAllowedTabs(prev => [...prev, tabId]);
    } else {
      setAllowedTabs(prev => prev.filter(tab => tab !== tabId));
    }
  };

  const handleSave = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      // First, delete all existing permissions for this user
      const { error: deleteError } = await supabase
        .from('user_permissions')
        .delete()
        .eq('user_id', user.user_id);

      if (deleteError) throw deleteError;

      // Then, insert new permissions
      if (allowedTabs.length > 0) {
        const permissions = allowedTabs.map(tab => ({
          user_id: user.user_id,
          module: tab,
          permission_level: 'full'
        }));

        const { error: insertError } = await supabase
          .from('user_permissions')
          .insert(permissions);

        if (insertError) throw insertError;
      }

      toast({
        title: "Success",
        description: "User permissions updated successfully"
      });

      onPermissionsUpdated();
      onClose();
    } catch (error: any) {
      console.error('Error updating permissions:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update permissions",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectAll = () => {
    setAllowedTabs(AVAILABLE_TABS.map(tab => tab.id));
  };

  const handleSelectNone = () => {
    setAllowedTabs([]);
  };

  if (!user) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Manage Tab Access for {user.first_name} {user.last_name}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {isSuperAdmin && (
            <div className="p-4 bg-purple-50 rounded-lg border">
              <p className="text-sm text-purple-800">
                <strong>Super Admin (stamatis)</strong> automatically has access to all tabs. This cannot be changed.
              </p>
            </div>
          )}

          {!isSuperAdmin && (
            <>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleSelectAll}
                >
                  Select All
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleSelectNone}
                >
                  Select None
                </Button>
              </div>

              <div className="space-y-4 max-h-96 overflow-y-auto">
                {AVAILABLE_TABS.map(tab => (
                  <div key={tab.id} className="flex items-start space-x-3">
                    <Checkbox
                      id={tab.id}
                      checked={allowedTabs.includes(tab.id)}
                      onCheckedChange={(checked) => handleTabToggle(tab.id, !!checked)}
                    />
                    <div className="space-y-1">
                      <Label htmlFor={tab.id} className="text-sm font-medium">
                        {tab.name}
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        {tab.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={handleSave} 
                  disabled={isLoading}
                  className="flex-1"
                >
                  {isLoading ? "Saving..." : "Save Permissions"}
                </Button>
                <Button variant="outline" onClick={onClose} className="flex-1">
                  Cancel
                </Button>
              </div>
            </>
          )}

          {isSuperAdmin && (
            <div className="flex justify-end">
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}